import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { MutationCache, QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import {
  asyncStoragePersister,
  shouldDehydrateQuery,
} from "@/lib/query-persister";
import { Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { AppZeroProvider } from "@/zero/provider";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { MainLayout } from "@/components/layout/MainLayout";
import { mainRoutes } from "./AppRoutes";
import { NewVersionBanner } from "@/components/NewVersionBanner";
import { friendlyMutationError } from "@/lib/error-utils";
import { toast } from "sonner";
import { lazyPage, PageLoading } from "@/lib/lazy-page";

const AuthPage = lazyPage(() => import("./pages/AuthPage"));
const ResetPasswordPage = lazyPage(() => import("./pages/ResetPasswordPage"));
const DevicePage = lazyPage(() => import("./pages/DevicePage"));
const NotFound = lazyPage(() => import("./pages/NotFound"));
const PublicCameraPlanPage = lazyPage(
  () => import("./pages/PublicCameraPlanPage"),
);
const CameraPlanPrintPage = lazyPage(
  () => import("./pages/CameraPlanPrintPage"),
);

const FIVE_SECONDS = 1000 * 5;
const TWENTY_FOUR_HOURS = 1000 * 60 * 60 * 24;

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.onError) return;
      toast.error(friendlyMutationError(error));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: FIVE_SECONDS,
      gcTime: TWENTY_FOUR_HOURS,
      retry: (failureCount, error) => {
        if (!navigator.onLine) return false;
        // Don't retry auth or not-found errors
        if (error instanceof Error) {
          const msg = error.message;
          if (msg.includes("401") || msg.includes("403") || msg.includes("404"))
            return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

const persistOptions = {
  persister: asyncStoragePersister,
  maxAge: TWENTY_FOUR_HOURS,
  dehydrateOptions: {
    shouldDehydrateQuery,
  },
};

/** What the side panel can show: any page of the main layout (R1). */
const panelRoutes = (
  <>
    <Route path="/">{mainRoutes}</Route>
    <Route path="*" element={<NotFound />} />
  </>
);

const App = () => (
  <ErrorBoundary>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions}
    >
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <NewVersionBanner />
        <BrowserRouter useTransitions={false}>
          <Routes>
            {/* Public share pages: no session, so no auth or Zero providers
                (they would request a login token and log a 401) */}
            <Route
              path="/share/camera-plans/:token"
              element={
                <Suspense fallback={<PageLoading fullScreen />}>
                  <PublicCameraPlanPage />
                </Suspense>
              }
            />
            <Route
              path="/share/camera-plans/:token/print"
              element={
                <Suspense fallback={<PageLoading fullScreen />}>
                  <CameraPlanPrintPage />
                </Suspense>
              }
            />
            <Route path="*" element={<AuthenticatedApp />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </PersistQueryClientProvider>
  </ErrorBoundary>
);

/** Everything else, with the session and Zero. */
const AuthenticatedApp = () => (
  <AuthProvider>
    <AppZeroProvider>
      <Suspense fallback={<PageLoading fullScreen />}>
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/device"
            element={
              <ProtectedRoute>
                <DevicePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout panelRoutes={panelRoutes} />
              </ProtectedRoute>
            }
          >
            {mainRoutes}
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AppZeroProvider>
  </AuthProvider>
);

export default App;
